"use client";

import { useCallback, useMemo } from "react";
import { ShellLink } from "@/src/components";
import { cn } from "utils";
import { HORIZONTAL_SCROLL_ATTR, useIsPc } from "@/src/hooks";
import { useNewsPrimaryTabs, type NewsThemeGroup } from "./useNewsPrimaryTabs";

const NEWS_PRIMARY_SWIPE_DIRECTION_STORAGE_KEY = "newsPrimarySwipeDirection";

export type { NewsThemeGroup } from "./useNewsPrimaryTabs";

export const AIInsightCategoryTab = ({
  className,
  groups,
}: {
  className?: string;
  groups: NewsThemeGroup[];
}) => {
  const [isPC] = useIsPc();
  const {
    tabs,
    pathPrefix,
    currentCategory,
    currentSubTag,
    activePrimaryTabKey,
    onPrimaryTabChange,
  } = useNewsPrimaryTabs({
    groups,
  });

  const preparePrimaryTabTransition = useCallback(
    (nextTabKey: string) => {
      if (typeof window === "undefined") {
        return;
      }
      if (!activePrimaryTabKey || activePrimaryTabKey === nextTabKey) {
        return;
      }
      const currentIndex = tabs.findIndex(
        (tab) => tab.key === activePrimaryTabKey,
      );
      const nextIndex = tabs.findIndex((tab) => tab.key === nextTabKey);
      if (currentIndex < 0 || nextIndex < 0 || currentIndex === nextIndex) {
        return;
      }
      const direction = nextIndex > currentIndex ? "left" : "right";
      try {
        window.sessionStorage.setItem(
          NEWS_PRIMARY_SWIPE_DIRECTION_STORAGE_KEY,
          direction,
        );
      } catch {
        // storage 不可用时降级为无动画，不影响切换主流程。
      }
    },
    [activePrimaryTabKey, tabs],
  );

  const handlePrimaryTabClick = useCallback(
    (
      event: React.MouseEvent<HTMLAnchorElement>,
      tabKey: string,
      isActive: boolean,
    ) => {
      event.stopPropagation();
      // 保留浏览器原生行为（新标签打开、下载等）
      if (
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.button !== 0
      ) {
        return;
      }
      event.preventDefault();
      if (isActive) {
        return;
      }
      if (!isPC) {
        // 仅准备方向，不在头部组件消费方向值，避免抢占页面容器动画。
        preparePrimaryTabTransition(tabKey);
      }
      onPrimaryTabChange(tabKey);
    },
    [isPC, onPrimaryTabChange, preparePrimaryTabTransition],
  );

  const activeGroup = useMemo(
    () => groups.find((group) => group.channel === currentCategory),
    [currentCategory, groups],
  );
  return (
    <>
      <div
        className={cn(
          "bg-background_solid_secondary w-full mx-auto",
          className,
        )}
      >
        <div className="flex gap-6 items-end">
          <tab-indicator
            className="flex gap-6 overflow-x-auto no-scrollbar [--underline-color:var(--edgen-border\_brand\_solid)] [--animation-duration:0.2s] self-end relative"
            effect="underline"
            data-tracking-scope="tabs"
            smart-horizontal-scroll={true}
          >
            {tabs.map((item) => {
              const isActive = item.key === currentCategory;
              return (
                <ShellLink
                  title={item.label}
                  aria-label={item.label}
                  data-openreplay-label={`${`${item.label} Link`} | ${item.path}`}
                  id="lnk-aiinsight-category-tab"
                  href={item.path}
                  key={item.key}
                  role="tab-item"
                  data-actived={isActive}
                  onClick={(e) => {
                    handlePrimaryTabClick(e, item.key, isActive);
                  }}
                  className={cn(
                    "tab_item flex-shrink-0 py-2 body_medium_l text-content_tertiary",
                    isActive
                      ? "border-b-2 border-border_brand_solid pb-[6px] text-content_primary"
                      : "pb-2",
                  )}
                  prefetch={true}
                  replace={true}
                >
                  {item.label}
                </ShellLink>
              );
            })}
          </tab-indicator>
        </div>
        <div className="h-[1px] bg-border_white_transparent10 -mx-4 md:-mx-15"></div>

        {!!activeGroup?.subTags.length && (
          <tab-indicator
            {...{ [HORIZONTAL_SCROLL_ATTR]: "true" }}
            className="flex w-full gap-2 overflow-x-auto no-scrollbar md:overflow-visible md:flex-wrap md:gap-3 my-4 [--underline-height:0] [--underline-color:transparent] [--animation-duration:0.2s] self-end relative"
            effect="underline"
            data-tracking-scope="news-subtags"
            smart-horizontal-scroll={true}
          >
            {activeGroup.subTags.map((item) => {
              const href = `${pathPrefix}/news/${activeGroup.channel}/${item.slug}`;
              const isSelected = item.slug === currentSubTag;
              return (
                <ShellLink
                  title={item.name}
                  aria-label={item.name}
                  data-openreplay-label={`${`${item.name} Link`} | ${href}`}
                  id="lnk-aiinsight-category-tab-2"
                  key={item.slug}
                  href={href}
                  role="tab-item"
                  data-actived={isSelected}
                  replace={true}
                  prefetch={true}
                  className={cn(
                    "tab_item flex-shrink-0 whitespace-nowrap body_medium_s transition-colors rounded-[6px] py-2 md:px-3 px-2",
                    isSelected
                      ? "bg-background_brand_overlay10 text-content_primary border border-border_brand_transparent50"
                      : "text-content_tertiary bg-background_white_overlay5",
                  )}
                >
                  <h2
                    aria-label="AIInsight Category Tab h2"
                    data-openreplay-label="AIInsight Category Tab h2 Semantic"
                    id="sem-aiinsight-category-tab-h2"
                  >
                    {item.name}
                  </h2>
                </ShellLink>
              );
            })}
          </tab-indicator>
        )}
      </div>
    </>
  );
};
